import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Lightning, HardDrives, CloudArrowUp, LinkSimple, Copy, ArrowLeft, ShieldWarning, Trash, Plus, ArrowClockwise } from '@phosphor-icons/react';

const ORANGE = '#FF9900';

export default function AmplifyDeployer() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('deploy');
  const [accounts, setAccounts] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [selectedAcc, setSelectedAcc] = useState('');
  const [newAcc, setNewAcc] = useState({ label: '', access_key: '', secret_key: '', region: 'us-east-1' });
  const [count, setCount] = useState(1);
  const [prefix, setPrefix] = useState('site');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [overrideFile, setOverrideFile] = useState(null);

  const loadAccounts = useCallback(() => {
    axios.get('/api/amplify/accounts', { withCredentials: true }).then(r => {
      setAccounts(r.data);
      if (r.data.length && !selectedAcc) setSelectedAcc(r.data[0].id);
    }).catch(() => {});
  }, [selectedAcc]);

  const loadDeployments = useCallback(() => {
    axios.get('/api/amplify/deployments', { withCredentials: true }).then(r => setDeployments(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadAccounts(); loadDeployments(); }, [loadAccounts, loadDeployments]);
  useEffect(() => {
    const iv = setInterval(loadDeployments, 5000);
    return () => clearInterval(iv);
  }, [loadDeployments]);

  const addAccount = async () => {
    if (!newAcc.label || !newAcc.access_key || !newAcc.secret_key) return toast.error('Fill all fields');
    setBusy(true);
    try {
      await axios.post('/api/amplify/accounts', newAcc, { withCredentials: true });
      toast.success('AWS account added & verified');
      setNewAcc({ label: '', access_key: '', secret_key: '', region: 'us-east-1' });
      loadAccounts();
      setTab('deploy');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add account'); }
    setBusy(false);
  };

  const deleteAccount = async (id) => {
    if (!window.confirm('Remove this AWS account?')) return;
    await axios.delete(`/api/amplify/accounts/${id}`, { withCredentials: true });
    loadAccounts();
  };

  const doDeploy = async () => {
    if (!selectedAcc) return toast.error('Select an AWS account');
    if (!file) return toast.error('Choose a ZIP file (index.html at root)');
    setBusy(true);
    const fd = new FormData();
    fd.append('account_id', selectedAcc);
    fd.append('count', count);
    fd.append('prefix', prefix);
    fd.append('file', file);
    try {
      const r = await axios.post('/api/amplify/deploy', fd, { withCredentials: true });
      toast.success(`Deploying ${r.data.count} app(s)…`);
      if (r.data.note) toast.warning(r.data.note);
      setTab('manage');
      loadDeployments();
    } catch (e) { toast.error(e.response?.data?.detail || 'Deploy failed'); }
    setBusy(false);
  };

  const runOverride = async () => {
    if (!selected.length) return toast.error('Select apps to re-upload');
    if (!overrideFile) return toast.error('Choose a new ZIP');
    setBusy(true);
    const fd = new FormData();
    fd.append('deployment_ids', selected.join(','));
    fd.append('file', overrideFile);
    try {
      await axios.post('/api/amplify/bulk-override', fd, { withCredentials: true });
      toast.success('Re-uploading to selected apps…');
      setSelected([]); setOverrideFile(null); loadDeployments();
    } catch (e) { toast.error(e.response?.data?.detail || 'Override failed'); }
    setBusy(false);
  };

  const deleteDep = async (id) => {
    if (!window.confirm('Delete this app from AWS?')) return;
    await axios.delete(`/api/amplify/deployments/${id}`, { withCredentials: true });
    loadDeployments();
  };

  const massWipe = async () => {
    if (!selectedAcc) return toast.error('Select an account');
    if (!window.confirm('DELETE ALL Amplify apps on this AWS account? This cannot be undone.')) return;
    try {
      const r = await axios.post(`/api/amplify/accounts/${selectedAcc}/wipe`, {}, { withCredentials: true });
      toast.success(`Wiping ${r.data.total} app(s)…`);
      loadDeployments();
    } catch (e) { toast.error(e.response?.data?.detail || 'Wipe failed'); }
  };

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const statusColor = (s) => s === 'live' ? 'text-green-400' : s === 'failed' ? 'text-red-400' : 'text-amber-400';

  const TabBtn = ({ id, icon, label }) => (
    <button onClick={() => setTab(id)} data-testid={`amplify-tab-${id}`}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider border-b-2 transition-colors ${tab === id ? 'border-[#FF9900] text-[#FF9900]' : 'border-transparent text-slate-400 hover:text-white'}`}>
      {icon} {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#0A0B0E] text-white p-8">
      <button onClick={() => navigate('/')} className="text-xs text-slate-500 hover:text-white uppercase tracking-widest flex items-center gap-1 font-bold mb-6"><ArrowLeft size={14} /> Switch Platform</button>
      <div className="flex items-center gap-3 mb-2">
        <Lightning size={34} weight="fill" className="text-[#FF9900]" />
        <h1 className="text-4xl font-black">AMPLIFY <span className="text-[#FF9900]">DEPLOYER</span></h1>
      </div>
      <p className="text-slate-500 font-mono text-sm mb-8">Deploy static sites to <span className="text-[#FF9900]">*.amplifyapp.com</span> · bulk override · mass wipe · limit 5 apps/account</p>

      <div className="flex gap-2 border-b border-white/10 mb-8">
        <TabBtn id="deploy" icon={<CloudArrowUp size={16} />} label="Deploy" />
        <TabBtn id="manage" icon={<LinkSimple size={16} />} label="Manage URLs" />
        <TabBtn id="accounts" icon={<HardDrives size={16} />} label="AWS Accounts" />
      </div>

      {/* Account selector (shared) */}
      {accounts.length > 0 && (
        <div className="mb-6 flex items-center gap-3">
          <span className="text-xs uppercase tracking-wider text-slate-400">Account</span>
          <select data-testid="amplify-account-select" value={selectedAcc} onChange={e => setSelectedAcc(e.target.value)}
            className="bg-[#12141A] border border-white/10 p-2 text-sm focus:outline-none focus:border-[#FF9900]">
            {accounts.map(a => <option key={a.id} value={a.id}>{a.label} ({a.region})</option>)}
          </select>
        </div>
      )}

      {tab === 'deploy' && (
        <div className="max-w-xl space-y-4">
          {accounts.length === 0 && <p className="text-amber-400 text-sm">Add an AWS account first (Accounts tab).</p>}
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400">Number of sites (max 5/account)</label>
            <input type="number" min="1" max="5" value={count} onChange={e => setCount(Number(e.target.value))} data-testid="amplify-count"
              className="w-full bg-[#12141A] border border-white/10 p-3 mt-1 focus:outline-none focus:border-[#FF9900]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400">Name prefix</label>
            <input value={prefix} onChange={e => setPrefix(e.target.value)} data-testid="amplify-prefix"
              className="w-full bg-[#12141A] border border-white/10 p-3 mt-1 font-mono focus:outline-none focus:border-[#FF9900]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400">Static site ZIP (index.html at root)</label>
            <input type="file" accept=".zip" onChange={e => setFile(e.target.files[0])} data-testid="amplify-file"
              className="w-full bg-[#12141A] border border-white/10 p-3 mt-1 text-sm" />
          </div>
          <button onClick={doDeploy} disabled={busy} data-testid="amplify-deploy-btn"
            className="bg-[#FF9900] text-black font-black px-6 py-3 uppercase tracking-wider hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
            <CloudArrowUp size={18} /> {busy ? 'Deploying…' : `Deploy ${count} Site(s)`}
          </button>
        </div>
      )}

      {tab === 'manage' && (
        <div>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <input type="file" accept=".zip" onChange={e => setOverrideFile(e.target.files[0])} data-testid="amplify-override-file"
              className="bg-[#12141A] border border-white/10 p-2 text-sm" />
            <button onClick={runOverride} disabled={busy || !selected.length} data-testid="amplify-override-btn"
              className="bg-[#FF9900] text-black font-bold px-4 py-2 text-sm uppercase tracking-wider hover:opacity-90 disabled:opacity-40 flex items-center gap-2">
              <ArrowClockwise size={16} /> Bulk Override ({selected.length})
            </button>
            <button onClick={loadDeployments} className="border border-white/20 px-3 py-2 text-sm hover:bg-white/10 flex items-center gap-2"><ArrowClockwise size={16} /> Refresh</button>
            <button onClick={massWipe} data-testid="amplify-wipe-btn"
              className="ml-auto bg-red-600/20 border border-red-500 text-red-400 font-bold px-4 py-2 text-sm uppercase tracking-wider hover:bg-red-600/30 flex items-center gap-2">
              <ShieldWarning size={16} /> Mass Wipe Account
            </button>
          </div>
          <div className="border border-white/10">
            {deployments.length === 0 && <p className="p-6 text-slate-500 text-sm">No deployments yet.</p>}
            {deployments.map(d => (
              <div key={d.id} data-testid={`amplify-dep-${d.id}`} className="flex items-center gap-3 p-3 border-b border-white/5 hover:bg-white/5">
                <input type="checkbox" checked={selected.includes(d.id)} onChange={() => toggle(d.id)} disabled={!d.app_id} />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm truncate">{d.app_name}</div>
                  {d.public_url && <a href={d.public_url} target="_blank" rel="noreferrer" className="text-[#FF9900] text-xs hover:underline truncate block">{d.public_url}</a>}
                  {(d.error || d.override_error) && <div className="text-red-400 text-xs">{d.error || d.override_error}</div>}
                </div>
                <span className={`text-xs font-bold uppercase ${statusColor(d.override_status && d.override_status !== 'done' ? d.override_status : d.status)}`}>
                  {d.override_status && d.override_status !== 'done' ? `override:${d.override_status}` : d.status}
                </span>
                {d.public_url && <button onClick={() => { navigator.clipboard.writeText(d.public_url); toast.success('Copied'); }} className="text-slate-400 hover:text-white p-1"><Copy size={16} /></button>}
                <button onClick={() => deleteDep(d.id)} className="text-slate-400 hover:text-red-400 p-1"><Trash size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'accounts' && (
        <div className="max-w-xl space-y-6">
          <div className="border border-white/10 p-6 space-y-3">
            <h3 className="font-bold uppercase tracking-wider text-sm flex items-center gap-2"><Plus size={16} /> Add AWS Account</h3>
            <input placeholder="Label" value={newAcc.label} onChange={e => setNewAcc({ ...newAcc, label: e.target.value })} data-testid="amplify-acc-label" className="w-full bg-[#12141A] border border-white/10 p-3 text-sm focus:outline-none focus:border-[#FF9900]" />
            <input placeholder="AWS Access Key ID" value={newAcc.access_key} onChange={e => setNewAcc({ ...newAcc, access_key: e.target.value })} data-testid="amplify-acc-key" className="w-full bg-[#12141A] border border-white/10 p-3 text-sm font-mono focus:outline-none focus:border-[#FF9900]" />
            <input placeholder="AWS Secret Access Key" type="password" value={newAcc.secret_key} onChange={e => setNewAcc({ ...newAcc, secret_key: e.target.value })} data-testid="amplify-acc-secret" className="w-full bg-[#12141A] border border-white/10 p-3 text-sm font-mono focus:outline-none focus:border-[#FF9900]" />
            <input placeholder="Region (e.g. us-east-1)" value={newAcc.region} onChange={e => setNewAcc({ ...newAcc, region: e.target.value })} data-testid="amplify-acc-region" className="w-full bg-[#12141A] border border-white/10 p-3 text-sm font-mono focus:outline-none focus:border-[#FF9900]" />
            <button onClick={addAccount} disabled={busy} data-testid="amplify-acc-add" className="bg-[#FF9900] text-black font-bold px-5 py-2 text-sm uppercase tracking-wider hover:opacity-90 disabled:opacity-50">{busy ? 'Verifying…' : 'Add & Verify'}</button>
          </div>
          <div className="space-y-2">
            {accounts.map(a => (
              <div key={a.id} className="flex items-center justify-between border border-white/10 p-3">
                <span className="text-sm">{a.label} <span className="text-slate-500 font-mono">· {a.region}</span></span>
                <button onClick={() => deleteAccount(a.id)} className="text-slate-400 hover:text-red-400 p-1"><Trash size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
